import React, { useEffect, useState } from 'react';

const W = 1000;
const H = 200;

const fmtDay = (ts) => new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/**
 * Mini gráfico de precio de los últimos 30 días (área + línea, SVG puro).
 * Usa el mismo endpoint que EnhancedTokenChart (/raw_token_prices?range=1M).
 */
const PriceSparkline = ({ blockchainService, symbol }) => {
  const [points, setPoints] = useState(null); // null = cargando, [] = sin datos

  useEffect(() => {
    if (!blockchainService) return undefined;
    let cancelled = false;
    setPoints(null);
    blockchainService.fetchFromChain('/raw_token_prices?range=1M')
      .then((data) => {
        if (cancelled) return;
        const rows = (Array.isArray(data) ? data : [])
          .map((d) => ({
            t: d.timestamp || (d.date ? new Date(d.date).getTime() : null),
            p: parseFloat(d.price)
          }))
          .filter((d) => d.t && Number.isFinite(d.p))
          .sort((a, b) => a.t - b.t);
        setPoints(rows);
      })
      .catch(() => { if (!cancelled) setPoints([]); });
    return () => { cancelled = true; };
  }, [blockchainService]);

  if (points === null) {
    return <div className="h-24 mt-2 bg-gray-100 rounded animate-pulse" />;
  }
  if (points.length < 2) {
    return <p className="h-24 mt-2 flex items-center text-sm text-gray-500">No price history available</p>;
  }

  const min = Math.min(...points.map((d) => d.p));
  const max = Math.max(...points.map((d) => d.p));
  const t0 = points[0].t;
  const t1 = points[points.length - 1].t;
  const x = (t) => ((t - t0) / (t1 - t0 || 1)) * W;
  // 8% de margen arriba y abajo para que la línea no toque el borde
  const y = (p) => H - 8 - ((p - min) / (max - min || 1)) * (H - 16);
  const line = points.map((d) => `${x(d.t).toFixed(1)},${y(d.p).toFixed(1)}`).join(' ');
  const digits = max < 1 ? 4 : 2;

  return (
    <>
      <svg
        role="img"
        aria-label={`${symbol} price over the last 30 days, between $${min.toFixed(digits)} and $${max.toFixed(digits)}`}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full h-24 mt-2 block"
      >
        <path d={`M0,${H} L${line.split(' ').join(' L')} L${W},${H} Z`} className="fill-indigo-50" />
        <polyline points={line} fill="none" className="stroke-indigo-700" strokeWidth="3" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      </svg>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{fmtDay(t0)}</span>
        <span>{fmtDay(t1)}</span>
      </div>
    </>
  );
};

export default React.memo(PriceSparkline);
